import { AppBar, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Radio, RadioGroup, Switch, Tab, Tabs, TextField, Toolbar, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from 'assets/appConfig';
import { AppImages } from 'assets/appResources';
import { FormStyleClasses, GlobalClassKeys, TabLargeStyle } from 'assets/jss/common-styles';
import { AppBarTabs } from 'components/AppTabs';
import ButtonBack from 'components/Buttons/ButtonBack';
import ButtonCancel from 'components/Buttons/ButtonCancel';
import ButtonSave from 'components/Buttons/ButtonSave';
import { RequestIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import { ImageSelection } from "components/FileUpload/ImageSelection";
import CardHeaderColor from 'components/MuiExtras/Card/CardHeaderColor';
import MuiTextInputToken from 'components/MuiExtras/CustomInput/MuiTextInputToken';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import MuiMuted from 'components/MuiExtras/Typography/MuiMuted';
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from 'components/TextFieldCounter';
import { TextPluralization } from 'components/TextPluralization';
import LibUtils from "lib/LibUtils";
import MediaUtils from 'lib/MediaUtils';
import { DynamicSystemRoles, MemberType, SystemRoles, TopicCommonUserApprovalMethod, TopicWhoAnalyzesApproval, TopicWhoDetermineSpecialist } from 'models/ApiEnums';
import { GetTopicDto, GroupItemChangedDto, SaveTopicDto, TopicRecordFormConfigDto } from 'models/ApiModels';
import { RequestLifeCycle } from 'models/AppModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import Session from 'services/Session';
import MemberAutoComplete from 'views/Member/Components/MemberAutoComplete';
import { TopicAbout } from 'views/Topic/Components/TopicAbout';
import TopicAutoComplete from 'views/Topic/Components/TopicAutoComplete';
import TopicEditMember from 'views/Topic/Components/TopicEditMember';
import TopicLogDialog from 'views/Topic/Dialogs/TopicLogDialog';
import TopicSpecialistsDialog from 'views/Topic/Dialogs/TopicSpecialistsDialog';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MaxChars = 1500;

enum TabOptions { Accesses, Properties }

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    topicId?: string;
    overrideSaveMessage?: string;
    dialogMode?: boolean;
    onClose: (postTopic: SaveTopicDto) => void;
}

interface IValidation {
    name: boolean;
    thumbnail: boolean;
}

interface IState {
    formCycle: RequestLifeCycle;
    lastError: any;
    record: Partial<GetTopicDto>;
    validation: IValidation;
    applyApprovalAnalyseGroupOnSubtopics: boolean;
    applySpecialistGroupOnSubtopics: boolean;
    dialog: any;
    currentTab: TabOptions;
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TopicEdit extends React.Component<Props, IState> {
    private loadDto: GetTopicDto = null;
    private config: TopicRecordFormConfigDto;
    private applyHierarchyTopicsOnCreatePubSetted: boolean = false;

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            currentTab: TabOptions.Accesses,
            record: null,
            validation: {
                name: null,
                thumbnail: true
            },
            applyApprovalAnalyseGroupOnSubtopics: false,
            applySpecialistGroupOnSubtopics: false
        };

    }

    ///
    private getEmptyDto(): GetTopicDto {
        const currentUser = Session.user;

        return {
            topicId: null,
            name: "",
            description: "",

            aka: [],
            containsSubtopics: [],
            isSubtopicOf: [],
            specialists: [],

            whoAnalyzesApproval: TopicWhoAnalyzesApproval.DefinedUser,
            approver: {
                id: currentUser.id,
                name: currentUser.name,
                thumbnailUrl: currentUser.thumbnailUrl,
                culture: currentUser.culture,
                isSystemUser: false
            },

            commonUserApprovalMethod: this.config.approverOnlySuspectPublication
                ? TopicCommonUserApprovalMethod.SuspiciousOnly
                : TopicCommonUserApprovalMethod.Never,

            whoDetermineSpecialist: TopicWhoDetermineSpecialist.None,
            specialistRequireLevelNinja: false,
            specialistRequireMinEndorssement: false,
            specialistRequireMinPublications: false,
            allowUserSpecialist: false,

            isPublic: true,
            specialistRequireManagerEndorssement: false,
            applyHierarchyTopicsOnCreatePub: false,

            changedAdministrators: [],
            changedMembers: [],

            recordInfo: null,
            editInDelegationMode: false,

            counters: {
                followers: 0,
                members: 0,
                publications: 0
            },

            thumbnail: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Topic.Components.TopicEdit." + r, f);
    }

    ///
    private init() {
        api.getTopicFormEditOptions()
            .then((config) => {
                this.config = config;

                if (this.props.topicId != null && this.props.topicId != "create")
                    this.continueLoadTopic(this.props.topicId);
                else {
                    const newDto = this.getEmptyDto();
                    this.loadDto = LibUtils.deepCopy(newDto);
                    this.setState({ formCycle: RequestLifeCycle.InitSuccess, record: newDto });
                }
            })
            .catch((error) => {
                this.setState({ formCycle: RequestLifeCycle.InitFailed, lastError: error });
            });
    }

    ///
    private continueLoadTopic(topicId: string) {
        this.setState({ formCycle: RequestLifeCycle.Init, lastError: null });

        api.getTopic(topicId)
            .then((record) => {
                this.loadDto = LibUtils.deepCopy(record);
                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record,
                    validation: {
                        name: record.name != null && record.name.length > 0,
                        thumbnail: true
                    }
                });
            })
            .catch((error) => {
                this.setState({ formCycle: RequestLifeCycle.InitFailed, lastError: error });
            });
    }

    //
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {

        this.setState((state) => {
            switch (name) {
                case "name":
                    state.record.name = v;
                    state.validation.name = v.length > 0;
                    break;

                case "thumbnail":
                    if (v == null) state.record.thumbnail = null;
                    else state.record.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;

                case "thumbnailUploading":
                    state.validation.thumbnail = !v;
                    break;

                case "whoDetermineSpecialist_UserManager":
                    if (v) state.record.whoDetermineSpecialist = state.record.whoDetermineSpecialist | TopicWhoDetermineSpecialist.UserManager;
                    else state.record.whoDetermineSpecialist = state.record.whoDetermineSpecialist ^ TopicWhoDetermineSpecialist.UserManager;
                    break;

                case "whoDetermineSpecialist_CorpManager":
                    if (v) state.record.whoDetermineSpecialist = state.record.whoDetermineSpecialist | TopicWhoDetermineSpecialist.CorpManager;
                    else state.record.whoDetermineSpecialist = state.record.whoDetermineSpecialist ^ TopicWhoDetermineSpecialist.CorpManager;
                    break;

                case "levelNinja":
                    state.record.specialistRequireLevelNinja = v;
                    break;

                case "minEndorssement":
                    state.record.specialistRequireMinEndorssement = v;
                    break;

                case "minPublications":
                    state.record.specialistRequireMinPublications = v;
                    break;

                case "changedAdminstrators":
                    state.record.changedAdministrators = v;
                    break;

                case "changedMembers":
                    state.record.changedMembers = v;
                    break;

                case "isPublic":
                    state.record.isPublic = v == "true";
                    break;

                case "isSubtopicOf":
                    state.record[name] = v;
                    if (this.loadDto.applyHierarchyTopicsOnCreatePub == false
                        && v.length > 0
                        && !this.applyHierarchyTopicsOnCreatePubSetted
                        && state.record.applyHierarchyTopicsOnCreatePub == false
                    ) {
                        state.record.applyHierarchyTopicsOnCreatePub = true;
                        this.applyHierarchyTopicsOnCreatePubSetted = true;
                    }
                    break;

                default:
                    state.record[name] = v;
                    break;
            }

            return state;
        });
    }

    ///
    private canSave(): boolean {
        const { validation } = this.state;

        return validation.name && validation.thumbnail;
    }

    ///
    private getPostDto() {

        const { record, applyApprovalAnalyseGroupOnSubtopics, applySpecialistGroupOnSubtopics } = this.state;

        const postDto: SaveTopicDto = {} as SaveTopicDto;

        LibUtils.copyProperties(record, postDto, ["thumbnail"]);

        postDto.approverId = record.approver == null ? null : record.approver.id;
        postDto.isSubtopicOf = record.isSubtopicOf.length == null ? null : record.isSubtopicOf.map((v) => v.key);
        postDto.containsSubtopic = record.containsSubtopics == null ? null : record.containsSubtopics.map((v) => v.key);

        postDto.applyApprovalAnalyseGroupOnSubtopics = applyApprovalAnalyseGroupOnSubtopics;
        postDto.applySpecialistGroupOnSubtopics = applySpecialistGroupOnSubtopics;

        if (record.thumbnail == null) postDto.thumbnail = null;
        else postDto.thumbnail = { fileId: record.thumbnail.fileId };

        postDto.changedAdminstrators = record.changedAdministrators;
        postDto.changedMembers = record.changedMembers;

        postDto.isPublic = record.isPublic;

        return postDto;
    }

    ///
    private save() {
        this.setState({ formCycle: RequestLifeCycle.Posting });

        var postDto = this.getPostDto();

        const doExit = () => {
            this.setState({ formCycle: RequestLifeCycle.None, dialog: null });
        };

        const doSave = () => {
            api.saveTopic(postDto)
                .then((newOrUpdateTopicId) => {
                    this.props.snackbar.showMessage(
                        this.res(
                            postDto.topicId == null
                                ? "CreateSuccess"
                                : "UpdateSuccess"
                        ),
                        "success"
                    );
                    this.props.onClose(postDto);
                    doExit();
                })
                .catch((error) => {
                    this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed, dialog: null });
                });
        };

        if (postDto.isPublic && this.state.record.counters.members > 0) {
            this.setState({
                dialog: (
                    <MuiConfirmation
                        message={this.res("ConfirmationPublicTopicMessage")}
                        onClose={
                            (response) => {
                                if (response) doSave();
                                else doExit();
                            }
                        }
                    />
                )
            });
        }
        else {
            doSave();
        }
    }

    ///
    private isRecordChanged() {
        return !LibUtils.deepCompare(this.state.record, this.loadDto);
    }

    ///
    private swapPostGroupOperation(group: "AllowUserSpecialist" | "ApprovalAnalyse" | "Specialist"): any {
        switch (group) {
            case "ApprovalAnalyse":
                this.setState({ applyApprovalAnalyseGroupOnSubtopics: !this.state.applyApprovalAnalyseGroupOnSubtopics });
                break;
            case "Specialist":
                this.setState({ applySpecialistGroupOnSubtopics: !this.state.applySpecialistGroupOnSubtopics });
                break;
        }
    }

    ///
    private handleSpecialist = () => {
        this.setState({
            dialog: (
                <TopicSpecialistsDialog
                    specialists={this.state.record.specialists}
                    onClose={
                        (data) => {
                            this.updateRecord("specialists", data);
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar
                position="sticky"
                color="default"
                className={GlobalClassKeys.stickyToolbar}
            >
                <Toolbar>

                    <div className={GlobalClassKeys.grow}>
                        <ButtonBack
                            text={this.res("ButtonGobackTitle")}
                            onClick={() => {
                                if (this.isRecordChanged()) {
                                    this.setState({
                                        dialog: <MuiConfirmation
                                            message={this.props.t("Common.Question.DiscardChanges")}
                                            onClose={
                                                (confirm) => {
                                                    if (confirm) this.props.onClose(undefined);
                                                    else this.setState({ dialog: null });
                                                }
                                            }
                                        />
                                    });
                                }
                                else {
                                    this.props.onClose(undefined);
                                }
                            }}
                        />
                    </div>

                    <ButtonSave
                        onClick={() => this.save()}
                        disabled={!this.canSave()}
                    />
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <TopicAbout
                        record={this.state.record.recordInfo}
                        counters={this.state.record.counters}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <TopicLogDialog
                                            topicId={this.state.record.topicId}
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private getPubSpecialistLineOption(): string {
        let specialistPubTextLine = (this.config.specialistMinPubs > 0)
            ? this.res("AutoDetermineSpecialistOp3a", { count: this.config.specialistMinPubs })
            : this.res("AutoDetermineSpecialistOp3");

        if (this.config.specialistMinLikes > 0 || this.config.specialistMinThanks > 0) specialistPubTextLine += `, ${this.props.t("Glossary.AtLeast")} `;

        if (this.config.specialistMinLikes > 0) {
            specialistPubTextLine += this.res("SpecialistMinLikes", { count: this.config.specialistMinLikes });
            if (this.config.specialistMinThanks > 0) specialistPubTextLine += ` ${this.props.t("Glossary.And")} `;
        }

        if (this.config.specialistMinThanks > 0)
            specialistPubTextLine += this.res("SpecialistMinThanks", { count: this.config.specialistMinThanks });

        if (this.config.specialistMinLikes > 0 || this.config.specialistMinThanks > 0) specialistPubTextLine += ` ${this.props.t("Glossary.InEach")}`;

        return specialistPubTextLine;
    }


    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.setState({ currentTab: value });
    };

    ///
    private renderFormAccesses() {
        const { record } = this.state;
        const { dialogMode } = this.props;
        const referenceUserId = record.topicId == null ? Session.user.id : this.state.record.recordInfo.creator.id;

        return (
            <>
                <MuiContainer margin="normal">
                    <Card>
                        <CardHeaderColor color="primary"
                            title={this.res("FormAccessTitle")}
                        />
                        <CardContent>

                            <FormControl fullWidth margin="normal">
                                <RadioGroup
                                    name="isPublic"
                                    value={record.isPublic.toString()}
                                    onChange={this.handleChange}
                                >
                                    <FormControlLabel value="true" control={<Radio />} label={this.res("IsPublicOptionTrue")} />
                                    <FormControlLabel value="false" control={<Radio />} label={this.res("IsPublicOptionFalse")} />
                                </RadioGroup>
                            </FormControl>

                        </CardContent>
                    </Card>
                </MuiContainer>

                {
                    !record.isPublic &&
                    <MuiContainer margin="normal">
                        <TopicEditMember
                            title={this.res("AdminDelegationTitle")}
                            topicId={record.topicId}
                            dialogMode={dialogMode}
                            membersChanged={record.changedAdministrators}
                            canDelete={(id: string) => { return id != referenceUserId; }}
                            onChange={(changedAdminstrators: GroupItemChangedDto[]) => this.updateRecord("changedAdminstrators", changedAdminstrators)}
                            onEndEdit={() => { }}
                            getMembers={api.getSimulatedTopicAdministrators}
                            getUsers={api.getSimulatedTopicAdministrators}
                        />
                    </MuiContainer>
                }

                {
                    !record.isPublic &&
                    <MuiContainer margin="normal">
                        <TopicEditMember
                            title={this.res("MembersTitle")}
                            topicId={record.topicId}
                            dialogMode={dialogMode}
                            membersChanged={record.changedMembers}
                            canDelete={(id: string) => { return (Session.hasRole(SystemRoles.TopicManager) || Session.hasDynamicRole(DynamicSystemRoles.TopicAdminDelegation)); }}
                            onChange={(changedMembers: GroupItemChangedDto[]) => this.updateRecord("changedMembers", changedMembers)}
                            onEndEdit={() => { }}
                            getMembers={api.getSimulatedTopicMembers}
                            getUsers={api.getSimulatedTopicUsers}
                        />
                    </MuiContainer>
                }

            </>
        );
    }

    ///
    private renderFormProperties() {
        const { record } = this.state;

        return (
            <>
                <MuiContainer margin="normal">
                    <Card>
                        <CardHeaderColor color="primary" title={this.res("FormPropsTitle")} />

                        <CardContent>

                            <MemberAutoComplete
                                id="approver"
                                label={this.res("ApproverLabel")}
                                emptySelectionLabel={this.res("pproverEmptyLabel")}
                                multiple={false}
                                margin={"normal"}
                                overModal
                                selectedItems={
                                    record.approver == null
                                        ? []
                                        : [{
                                            key: record.approver.id,
                                            label: record.approver.name,
                                            thumbnailUrl: record.approver.thumbnailUrl,
                                            memberType: MemberType.User,
                                            displayName: record.approver.name,
                                            roles: SystemRoles.None,
                                            canDelete: true
                                        }]
                                }
                                onChange={
                                    (items) => {
                                        if (items.length == 0)
                                            this.updateRecord("approver", null);
                                        else {
                                            const v = items[0];
                                            this.updateRecord("approver", { id: v.key, name: v.label });
                                        }
                                    }
                                }
                            />

                            <FormControl fullWidth margin="normal">
                                <FormLabel>{this.res("WhoAnalyzesApprovalLabel")}</FormLabel>
                                <RadioGroup
                                    name="whoAnalyzesApproval"
                                    value={record.whoAnalyzesApproval.toString()}
                                    onChange={this.handleChange}
                                >
                                    <FormControlLabel value={TopicWhoAnalyzesApproval.DefinedUser.toString()} control={<Radio />} label={this.res("WhoAnalyzesApprovalOp1")} />
                                    <FormControlLabel
                                        value={TopicWhoAnalyzesApproval.ManagerOfPublisher.toString()}
                                        control={<Radio style={{ alignSelf: "flex-start", paddingTop: 0 }} />}
                                        label={
                                            <>
                                                {this.res("WhoAnalyzesApprovalOp2")}
                                                <br />
                                                <Typography variant="body2">
                                                    <MuiMuted><small>{this.res("WhoAnalyzesApprovalOp2Help")}</small></MuiMuted>
                                                </Typography>
                                            </>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>


                            <FormControl fullWidth margin="normal">
                                <FormLabel>{this.res("ApprovalPubLabel")}</FormLabel>
                                <div>
                                    <MuiMuted>
                                        <small>
                                            {this.res("ApprovalPubSubLabel")}
                                        </small>
                                    </MuiMuted>
                                </div>
                                <RadioGroup
                                    name="commonUserApprovalMethod"
                                    value={record.commonUserApprovalMethod.toString()}
                                    onChange={this.handleChange}
                                >
                                    <FormControlLabel value={TopicCommonUserApprovalMethod.Never.toString()} control={<Radio />} label={this.res("ApprovalPubOp1")} />
                                    <FormControlLabel value={TopicCommonUserApprovalMethod.SuspiciousOnly.toString()} control={<Radio />} label={this.res("ApprovalPubOp2")} />
                                    <FormControlLabel value={TopicCommonUserApprovalMethod.Always.toString()} control={<Radio />} label={this.res("ApprovalPubOp4")} />
                                    <FormControlLabel value={TopicCommonUserApprovalMethod.NotApprovedAnotherTopic.toString()} control={<Radio />} label={this.res("ApprovalPubOp3")} />

                                </RadioGroup>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={this.state.applyApprovalAnalyseGroupOnSubtopics}
                                        onChange={() => { this.swapPostGroupOperation("ApprovalAnalyse"); }}
                                        color="secondary"
                                    />
                                }
                                label={<MuiColor variant="secondary">{this.res("ApplyChangeOnSubtopicsMessage")}</MuiColor>}
                            />

                        </CardContent>
                    </Card>
                </MuiContainer>

                <MuiContainer margin="normal">
                    <Card>
                        <CardHeaderColor color="primary" title={this.res("SpecialistTitle")} />

                        <CardContent>

                            <MuiContainer margin="normal">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.record.allowUserSpecialist}
                                            onChange={
                                                () => { this.updateRecord("allowUserSpecialist", !this.state.record.allowUserSpecialist); }
                                            }
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("UserSpecialistLabel")}
                                />
                            </MuiContainer>

                            {
                                this.state.record.allowUserSpecialist &&
                                (
                                    <>
                                        <MuiContainer margin="normal">
                                            <div>
                                                <MuiLink color="secondary" onClick={this.handleSpecialist}>{this.res("UserSpecialistEditLabel")}</MuiLink>
                                            </div>
                                            <div>
                                                <MuiMuted>
                                                    <TextPluralization count={record.specialists.length} resource="Views.Topic.Components.TopicEdit.UserSpecialistList" />
                                                </MuiMuted>
                                            </div>
                                        </MuiContainer>

                                        <FormControl fullWidth margin="normal">
                                            <FormLabel>{this.res("WhoDetermineSpecialistLabel")}</FormLabel>
                                            <FormGroup>
                                                <FormControlLabel control={<Checkbox name="whoDetermineSpecialist_UserManager" checked={(record.whoDetermineSpecialist & TopicWhoDetermineSpecialist.UserManager) > 0} onChange={this.handleChange} />} label={this.res("WhoDetermineSpecialistLabelOp1")} />
                                                <FormControlLabel control={<Checkbox name="whoDetermineSpecialist_CorpManager" checked={(record.whoDetermineSpecialist & TopicWhoDetermineSpecialist.CorpManager) > 0} onChange={this.handleChange} />} label={this.res("WhoDetermineSpecialistLabelOp2")} />
                                            </FormGroup>
                                        </FormControl>


                                        <FormControl fullWidth margin="normal">
                                            <FormLabel>{this.res("AutoDetermineSpecialistLabel")}</FormLabel>
                                            <FormGroup>
                                                <FormControlLabel control={<Checkbox name="specialistRequireLevelNinja" checked={record.specialistRequireLevelNinja} onChange={this.handleChange} />} label={this.res("AutoDetermineSpecialistOp1")} />
                                                <FormControlLabel control={<Checkbox name="specialistRequireMinEndorssement" checked={record.specialistRequireMinEndorssement} onChange={this.handleChange} />} label={this.res("AutoDetermineSpecialistOp2")} />
                                                <FormControlLabel control={<Checkbox name="specialistRequireMinPublications" checked={record.specialistRequireMinPublications} onChange={this.handleChange} />} label={this.getPubSpecialistLineOption()} />
                                                <FormControlLabel control={<Checkbox name="specialistRequireManagerEndorssement" checked={record.specialistRequireManagerEndorssement} onChange={this.handleChange} />} label={this.res("AutoDetermineSpecialistOp4")} />
                                            </FormGroup>
                                        </FormControl>
                                    </>
                                )
                            }

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={this.state.applySpecialistGroupOnSubtopics}
                                        onChange={() => { this.swapPostGroupOperation("Specialist"); }}
                                        color="secondary"
                                    />
                                }
                                label={<MuiColor variant="secondary">{this.res("ApplyChangeOnSubtopicsMessage")}</MuiColor>}
                            />

                        </CardContent>
                    </Card>
                </MuiContainer>

                <MuiContainer margin="normal">

                    <Card>
                        <CardHeaderColor title={this.res("FormFeaturesTitle")} color="primary" />

                        <CardContent>

                            <MuiTextInputToken
                                id="synonymous"
                                label={this.res("SynonymousLabel")}
                                placeHolder={this.res("SynonymousPlaceholder")}
                                selectedItems={record.aka}
                                onUpdateSelection={
                                    (items) => this.updateRecord("aka", items)
                                }
                                margin={"normal"}
                            />

                            <TopicAutoComplete
                                id="is_subtopic"
                                label={this.res("SubtopicOfLabel")}
                                placeholder={this.res("SelectTopicPlaceholder")}
                                margin={"normal"}
                                overModal
                                showAvatar={false}
                                selectedItems={
                                    record.isSubtopicOf.length == 0 ? [] : record.isSubtopicOf.map((v) => ({ key: v.key, label: v.value, canDelete: true }))
                                }
                                onChange={
                                    (items) => this.updateRecord("isSubtopicOf", items.map((v) => { return { key: v.key, value: v.label }; }))
                                }

                                onValidate={
                                    (item) => {
                                        const isValid = item.key != this.props.topicId && record.containsSubtopics.findIndex(v => v.key == item.key) == -1;
                                        if (!isValid) this.props.snackbar.showMessage(this.res("SelectTopicError"), "error");
                                        return isValid;
                                    }

                                }
                            />

                            {
                                record.isSubtopicOf.length > 0 &&
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="applyHierarchyTopicsOnCreatePub"
                                            checked={record.applyHierarchyTopicsOnCreatePub}
                                            onChange={this.handleChange}
                                            color="secondary"
                                        />
                                    }
                                    label={<MuiColor variant="secondary"><small>{this.res("ApplyHierarchyTopicsOnCreatePub")}</small></MuiColor>}
                                />
                            }

                            <TopicAutoComplete
                                id="contains_subtopic"
                                label={this.res("ContainsSubtopicLabel")}
                                margin={"normal"}
                                placeholder={this.res("SelectTopicPlaceholder")}
                                showAvatar={false}
                                overModal
                                selectedItems={
                                    record.containsSubtopics.length == 0 ? [] : record.containsSubtopics.map((v) => ({ key: v.key, label: v.value, canDelete: true }))
                                }
                                onChange={
                                    (items) =>
                                        this.updateRecord(
                                            "containsSubtopics",
                                            items.map((v) => { return { key: v.key, value: v.label }; })
                                        )
                                }
                                onValidate={
                                    (item) => {
                                        const isValid = item.key != this.props.topicId && record.isSubtopicOf.findIndex(v => v.key == item.key) == -1;
                                        if (!isValid) this.props.snackbar.showMessage(this.res("SelectTopicError"), "error");
                                        return isValid;
                                    }
                                }
                            />

                        </CardContent>
                    </Card>

                </MuiContainer>

            </>
        );
    }

    ///
    private renderForm() {
        const { record, validation } = this.state;

        return (

            <form noValidate autoComplete="off" >


                <MuiContainer margin="normal">
                    <Card>
                        <CardContent>
                            <Grid
                                container
                                spacing={3}
                                direction="row"
                            >
                                <Grid item>
                                    <ImageSelection
                                        acceptedFiles={MediaUtils.AcceptImages()}
                                        allowEditSelectedImage={true}
                                        currentImageUrl={record && record.thumbnail ? record.thumbnail.url : null}
                                        dimensions={SquarePicture}
                                        emptyImageUrl={AppImages.DefaultTopic}
                                        enableClear={true}
                                        onClear={() => { this.updateRecord("thumbnail", null); }}
                                        onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                        onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                        onFileUploadSuccess={(data) => this.updateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                    />

                                </Grid>
                                <Grid item className={GlobalClassKeys.grow}>

                                    <TextField
                                        name="name"
                                        label={this.res("NameLabel")}
                                        placeholder={this.res("NamePlaceholder")}
                                        autoComplete="off"
                                        value={record.name}
                                        autoFocus
                                        fullWidth
                                        error={validation.name == false}
                                        inputProps={{ maxLength: 255 }}
                                        onChange={this.handleChange}
                                        margin={"normal"}
                                    />

                                    <TextFieldCounter
                                        fullWidth
                                        multiline
                                        name="description"
                                        label={this.res("DescriptionLabel")}
                                        autoComplete="off"
                                        placeholder={this.res("DescriptionPlaceholder")}
                                        value={record.description}
                                        maxLength={MaxChars}
                                        onChange={(v) => this.updateRecord("description", v)}
                                        margin={"normal"}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </MuiContainer>

                <MuiContainer margin="dense">
                    <AppBar position="static" color="secondary" >
                        <AppBarTabs centered value={this.state.currentTab} onChange={this.handleTabSelection}>
                            <Tab value={TabOptions.Accesses} label={this.res("TabAccess")} style={TabLargeStyle} />
                            <Tab value={TabOptions.Properties} label={this.res("TabProps")} style={TabLargeStyle} />
                        </AppBarTabs>
                    </AppBar>
                </MuiContainer>
                {
                    this.state.currentTab == TabOptions.Accesses
                        ? this.renderFormAccesses()
                        : this.renderFormProperties()
                }

                {this.renderRecordInfo()}

            </form>
        );
    }

    ///
    public mainRender() {
        const { dialog, formCycle } = this.state;

        if (formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport
                error={this.state.lastError}
                action={
                    <ButtonBack
                        text={this.res("ButtonGobackTitle")}
                        onClick={() => this.props.onClose(undefined)}
                    />
                }
            />;
        }

        if (formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator />;
        }

        return (
            <>
                {dialog}

                {dialog == null && formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={this.state.formCycle == RequestLifeCycle.PostFailed}
                    error={this.state.lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                <Grid container justifyContent="space-around">
                    <Grid item xs>
                        <div className={this.props.dialogMode ? null : this.props.classes.formContainer}>
                            {this.props.dialogMode !== true && this.renderToolbar()}
                            {this.renderForm()}
                        </div>
                    </Grid>
                </Grid>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public render() {
        return (
            <>
                <Prompt
                    when={this.isRecordChanged()}
                    message={this.props.t("Common.Question.Unsave")}
                />

                {
                    (this.props.dialogMode)
                        ? (
                            <Dialog
                                open
                                fullWidth
                                maxWidth="md"
                            >
                                <DialogTitle>
                                    {this.res("DialogModeTitle")}
                                </DialogTitle>

                                <DialogContent>
                                    {this.mainRender()}
                                </DialogContent>

                                <DialogActions>
                                    <ButtonCancel
                                        onClick={() => this.props.onClose(null)}
                                    />
                                    <ButtonSave
                                        onClick={() => this.save()}
                                        disabled={!this.canSave()}
                                    />
                                </DialogActions>

                            </Dialog>
                        )
                        : this.mainRender()
                }
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(TopicEdit)));